/*
  Atlas compare — drag-to-compare slider behaviour.

  Each [data-atlas-compare] section exposes:
    - a .atlas-compare__visual whose --atlas-compare-pos CSS var (0%..100%)
      clips the after-image and positions the brass handle;
    - a hidden .atlas-compare__range <input type=range> for keyboard and
      assistive-tech control (arrow keys move the divider natively).

  Pointer events give real mouse + touch + pen drag ("grab anywhere"); the
  range input stays in sync so both paths drive the same position.
*/
(function () {
  "use strict";

  function initCompare(root) {
    var visual = root.querySelector(".atlas-compare__visual");
    var range = root.querySelector(".atlas-compare__range");
    if (!visual || !range) return;

    var dragging = false;
    var rect = null; // cached on pointerdown so we don't reflow every move
    var rafId = 0;
    var pendingPct = null;

    function applyPos(pct) {
      visual.style.setProperty("--atlas-compare-pos", pct + "%");
      var rounded = Math.round(pct);
      if (Number(range.value) !== rounded) range.value = rounded;
    }

    function setPos(pct) {
      if (pct < 0) pct = 0;
      if (pct > 100) pct = 100;
      applyPos(pct);
    }

    // During a drag, coalesce moves into one write per frame (rAF) so we don't
    // thrash style/paint faster than the display refreshes.
    function schedulePos(pct) {
      pendingPct = pct < 0 ? 0 : pct > 100 ? 100 : pct;
      if (rafId) return;
      rafId = requestAnimationFrame(function () {
        rafId = 0;
        if (pendingPct !== null) applyPos(pendingPct);
      });
    }

    function pctFromClientX(clientX) {
      var r = rect || visual.getBoundingClientRect();
      if (r.width === 0) return 50;
      return ((clientX - r.left) / r.width) * 100;
    }

    // Keyboard / AT: the native range input fires "input" on arrow keys.
    range.addEventListener("input", function () {
      setPos(Number(range.value));
    });

    // Pointer drag: grab anywhere on the visual, mouse or touch.
    visual.addEventListener("pointerdown", function (event) {
      dragging = true;
      rect = visual.getBoundingClientRect(); // read layout once per drag
      // is-dragging disables the clip-path transition so each move is instant
      // (the eased transition is what janks a continuous drag on mobile).
      visual.classList.add("is-dragging");
      try {
        visual.setPointerCapture(event.pointerId);
      } catch (err) {
        /* setPointerCapture unsupported — drag still works via window fallback */
      }
      setPos(pctFromClientX(event.clientX));
      range.focus({ preventScroll: true });
    });

    visual.addEventListener("pointermove", function (event) {
      if (!dragging) return;
      event.preventDefault();
      schedulePos(pctFromClientX(event.clientX));
    });

    function endDrag(event) {
      if (!dragging) return;
      dragging = false;
      rect = null;
      if (rafId) {
        cancelAnimationFrame(rafId);
        rafId = 0;
      }
      if (pendingPct !== null) {
        applyPos(pendingPct);
        pendingPct = null;
      }
      visual.classList.remove("is-dragging");
      try {
        visual.releasePointerCapture(event.pointerId);
      } catch (err) {
        /* nothing captured */
      }
    }

    visual.addEventListener("pointerup", endDrag);
    visual.addEventListener("pointercancel", endDrag);

    // Initialise from the input's starting value.
    setPos(Number(range.value) || 50);
  }

  function init() {
    var roots = document.querySelectorAll("[data-atlas-compare]");
    for (var i = 0; i < roots.length; i++) initCompare(roots[i]);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
